import apiClient from './api';
import type { SpecialRegulation, ExtractionStatus, SpecialRegulationsVersion } from '../types';

/**
 * Service module for special regulations API operations.
 *
 * Requirement 11.5 — Provide a regulationService module with functions for uploading
 * special regulations, checking extraction status, fetching version info, listing
 * sections with filters, and fetching a single section by number.
 */

export const regulationService = {
  /** Upload a special regulations PDF using presigned URL. */
  uploadSpecialRegulations: async (
    file: File,
    effectiveDate: string
  ): Promise<{ versionId: string; status: string }> => {
    // Step 1: Request presigned URL from backend
    const urlResponse = await apiClient.post<{
      upload_url: string;
      version_id: string;
      document_id: string;
    }>('/admin/special-regulations/upload-url', {
      file_name: file.name,
      effective_date: effectiveDate,
    });

    const { upload_url, version_id, document_id } = urlResponse.data;

    // Step 2: Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/pdf',
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.statusText}`);
    }

    // Step 3: Confirm upload completion with backend
    await apiClient.post('/admin/special-regulations/confirm', {
      document_id,
    });

    return { versionId: version_id, status: 'processing' };
  },

  /** Get the extraction status of the most recent extraction job. */
  getExtractionStatus: async (): Promise<ExtractionStatus> => {
    const response = await apiClient.get<ExtractionStatus>(
      '/admin/special-regulations/status'
    );
    return response.data;
  },

  /** Get the current special regulations version info. */
  getVersionInfo: async (): Promise<SpecialRegulationsVersion | null> => {
    const response = await apiClient.get<SpecialRegulationsVersion & { version?: null }>(
      '/admin/special-regulations/version'
    );
    // Backend returns {"version": null} when no version exists,
    // or {versionId, effectiveDate, sectionCount, uploadedAt} when one exists
    if ('version' in response.data && response.data.version === null) {
      return null;
    }
    return response.data as SpecialRegulationsVersion;
  },

  /** Get all special regulations versions. */
  getAllVersions: async (): Promise<SpecialRegulationsVersion[]> => {
    const response = await apiClient.get<{ versions: SpecialRegulationsVersion[] }>(
      '/admin/special-regulations/versions'
    );
    return response.data.versions;
  },

  /** List regulation sections with optional filters. */
  listSections: async (
    filters?: { category?: number; requirementType?: string; versionId?: string }
  ): Promise<SpecialRegulation[]> => {
    const params: Record<string, string> = {};
    if (filters?.category !== undefined) {
      params.category = String(filters.category);
    }
    if (filters?.requirementType) {
      params.requirementType = filters.requirementType;
    }
    if (filters?.versionId) {
      params.versionId = filters.versionId;
    }
    const response = await apiClient.get<{ sections: SpecialRegulation[] }>(
      '/regulations/sections',
      { params }
    );
    return response.data.sections;
  },

  /** Get a single regulation section by section number. */
  getSection: async (sectionNumber: string): Promise<SpecialRegulation> => {
    const response = await apiClient.get<SpecialRegulation>(
      `/regulations/sections/${encodeURIComponent(sectionNumber)}`
    );
    return response.data;
  },

  /** Ask a natural language question about regulations. */
  askQuestion: async (
    question: string,
    category?: number,
    signal?: AbortSignal
  ): Promise<{
    answer: string;
    sources: Array<{ documentType: string; sectionNumber: string }>;
    sectionsSearched: number;
    tokenUsage?: { input: number; output: number; total: number };
    cost?: number;
    timing?: { loadSections: number; generateEmbedding: number; similaritySearch: number; claudeResponse: number; total: number };
  }> => {
    const response = await apiClient.post<{
      answer: string;
      sources: Array<{ documentType: string; sectionNumber: string }>;
      sectionsSearched: number;
      tokenUsage?: { input: number; output: number; total: number };
      cost?: number;
      timing?: { loadSections: number; generateEmbedding: number; similaritySearch: number; claudeResponse: number; total: number };
    }>('/regulations/ask', { question, category }, { signal });
    return response.data;
  },

  /** Delete a special regulations version. */
  deleteVersion: async (versionId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      `/admin/special-regulations/${encodeURIComponent(versionId)}`
    );
    return response.data;
  },
};
